import { AuditInstance, SEOModule } from '../types';
import { ScoringEngine } from './scoringEngine';
import {
  getScoreColor,
  getScoreBgColor,
  getScoreBorderColor,
  getScoreLabel,
  hasRoomForImprovement,
  processStepFields,
} from '../utils/scoreUtils';

export class PdfService {
  /**
   * Export audit report to PDF using native print CSS window with premium dark mode styling
   */
  static exportToPdf(audit: AuditInstance, module: SEOModule): void {
    const scores = ScoringEngine.calculateScores(audit, module);
    const dateStr = new Date(audit.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Pop-up blocked. Please allow pop-ups to export the PDF report.');
      return;
    }

    // Calculate SVG Donut Circle parameters
    const strokeDasharray = 283;
    const strokeDashoffset = strokeDasharray - (strokeDasharray * scores.overallScore) / 100;

    const formatFieldValue = (val: any): string => {
      if (Array.isArray(val)) {
        return val.join(', ');
      }
      const sVal = String(val);
      switch (sVal) {
        case 'dynamic_regular': return 'Dynamic Site — Active Content Growth (Articles published regularly)';
        case 'static_stalled': return 'Static Site — Stalled Content Growth (Posts dated months/years ago)';
        case 'static_fixed_only': return 'Static Site — Fixed Pages Only (No blog/news section)';
        case 'clear_target': return 'Clear Keyword Target (Consistently present across title/H1/meta)';
        case 'partial_target': return 'Partial Keyword Target (Present in title/H1 but missing in meta/alt)';
        case 'unclear_target': return 'Unclear Keyword Target (Generic terms used; no clear query target)';
        case 'normal_indexation': return 'Normal Indexation (Google index aligns with crawl)';
        case 'under_indexed': return 'Under-Indexed (Google index lower than crawl)';
        case 'over_indexed_params': return 'Over-Indexed (Google index higher due to parameters/attachments)';
        case 'active_linked': return 'Active Social Presence Linked';
        case 'unlinked_broken': return 'Social Icons Present but Links Broken/Unlinked';
        case 'no_social_presence': return 'No Social Media Accounts Found';
        case 'blocked_bypass': return 'Bypass — Siteliner blocked by client firewall/server security';
        case 'unreachable_bypass': return 'Bypass — Website requires login / no server access yet';
        case 'accessible': return 'Accessible — Scanned successfully';
        default: return sVal;
      }
    };

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Zynex Ninja — Executive SEO Audit Report (${audit.clientName})</title>
        <style>
          @page {
            size: A4;
            margin: 10mm;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }
          body {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            background: #070a12;
            color: #f8fafc;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
          }
          .container {
            max-width: 1000px;
            margin: 0 auto;
            background: rgba(15, 23, 42, 0.95);
            border: 1px solid #1e293b;
            border-radius: 16px;
            padding: 36px;
          }
          .no-print {
            background: #0284c7;
            color: white;
            padding: 12px;
            text-align: center;
            font-weight: 600;
            border-radius: 8px;
            margin-bottom: 24px;
          }
          @media print {
            .no-print {
              display: none !important;
              visibility: hidden !important;
              height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            body {
              padding: 10px !important;
              margin: 0 !important;
              background: #070a12 !important;
            }
            .container {
              border: none !important;
              box-shadow: none !important;
              padding: 10px 15px !important;
              max-width: 100% !important;
              background: #070a12 !important;
            }
          }
          .header {
            border-bottom: 1px solid #1e293b;
            padding-bottom: 24px;
            margin-bottom: 30px;
          }
          .badge-row {
            display: flex;
            gap: 10px;
            margin-bottom: 10px;
          }
          .badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .badge-framework {
            background: rgba(56, 189, 248, 0.15);
            color: #38bdf8;
            border: 1px solid rgba(56, 189, 248, 0.3);
          }
          .badge-version {
            background: rgba(16, 185, 129, 0.15);
            color: #34d399;
            border: 1px solid rgba(16, 185, 129, 0.3);
          }
          h1 {
            font-size: 30px;
            color: #ffffff;
            font-weight: 800;
            margin: 0 0 6px 0;
            letter-spacing: -0.5px;
          }
          .subtitle {
            font-size: 16px;
            color: #38bdf8;
            font-weight: 600;
          }
          .meta-text {
            font-size: 12px;
            color: #94a3b8;
            margin-top: 4px;
          }

          /* Scorecard Section */
          .scorecard {
            background: linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(2, 6, 23, 0.9) 100%);
            border: 1px solid #1e293b;
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 35px;
          }
          .score-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
            align-items: center;
          }
          .gauge-box {
            display: flex;
            align-items: center;
            gap: 20px;
          }
          .gauge-text {
            display: flex;
            flex-direction: column;
          }

          .progress-group {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
          .bar-track {
            height: 8px;
            background: rgba(255,255,255,0.1);
            border-radius: 4px;
            overflow: hidden;
            margin-top: 4px;
          }
          .bar-fill {
            height: 100%;
            border-radius: 4px;
          }

          /* Section Comparison Graph */
          .section-graph {
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px solid #1e293b;
          }
          .section-graph-title {
            font-size: 12px;
            font-weight: 700;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 12px;
          }
          .graph-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .graph-item {
            background: rgba(2, 6, 23, 0.6);
            padding: 8px 12px;
            border-radius: 6px;
            border: 1px solid #1e293b;
          }

          /* Table of Contents */
          .toc {
            background: rgba(15, 23, 42, 0.6);
            border: 1px solid #1e293b;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 35px;
          }
          .toc-title {
            font-size: 14px;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 12px;
          }
          .toc-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .toc-item {
            display: flex;
            justify-content: space-between;
            background: rgba(2, 6, 23, 0.5);
            border: 1px solid #1e293b;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            color: #cbd5e1;
          }

          /* Executive Summary */
          .summary-box {
            background: rgba(2, 6, 23, 0.6);
            border: 1px solid #1e293b;
            border-radius: 8px;
            padding: 20px;
            font-size: 14px;
            color: #e2e8f0;
            line-height: 1.7;
            margin-bottom: 40px;
          }

          /* Step Findings Cards */
          .sec-header {
            font-size: 20px;
            color: #ffffff;
            font-weight: 700;
            border-bottom: 2px solid #0284c7;
            padding-bottom: 8px;
            margin-top: 36px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .finding-card {
            background: rgba(15, 23, 42, 0.75);
            border: 1px solid #1e293b;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 16px;
            page-break-inside: avoid;
          }
          .finding-card-title {
            font-size: 15px;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 12px;
            display: flex;
            justify-content: space-between;
          }
          .fields-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 10px;
            margin-bottom: 12px;
          }
          .field-item {
            background: #020617;
            padding: 8px 12px;
            border-radius: 4px;
            border: 1px solid #1e293b;
          }
          .field-label {
            font-size: 10px;
            color: #64748b;
            text-transform: uppercase;
            display: block;
          }
          .field-val {
            font-size: 13px;
            font-weight: 600;
            color: #f1f5f9;
          }

          .url-box {
            background: rgba(2, 6, 23, 0.75);
            border: 1px solid rgba(56, 189, 248, 0.25);
            border-radius: 6px;
            padding: 10px 14px;
            font-family: monospace;
            font-size: 12px;
            color: #38bdf8;
            margin: 10px 0;
            word-break: break-all;
          }
          .rec-box {
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid #1e293b;
            font-size: 13px;
            color: #34d399;
          }
          .evidence-img {
            max-width: 100%;
            max-height: 320px;
            border-radius: 6px;
            border: 1px solid #1e293b;
            margin-top: 10px;
          }
          .footer {
            text-align: center;
            margin-top: 50px;
            padding-top: 20px;
            border-top: 1px solid #1e293b;
            font-size: 11px;
            color: #64748b;
          }
        </style>
      </head>
      <body>
        <div class="no-print">
          Zynex Ninja — Executive PDF Preview. Press Command+P (Mac) or Ctrl+P (Windows) to Save PDF.
        </div>

        <div class="container">
          <div class="header">
            <div class="badge-row">
              <span class="badge badge-framework">Zynex Ninja Enterprise Framework</span>
              <span class="badge badge-version">Engine Version: ${audit.methodologyVersion}</span>
            </div>
            <h1>Comprehensive Technical SEO Audit Report</h1>
            <div class="subtitle">${audit.clientName} (${audit.websiteUrl})</div>
            <div class="meta-text">Generated on ${dateStr}</div>
          </div>

          <!-- Scorecard Dashboard -->
          <div class="scorecard">
            <div class="score-grid">
              <div class="gauge-box">
                <div style="position: relative; width: 110px; height: 110px;">
                  <svg width="110" height="110" viewBox="0 0 100 100" style="transform: rotate(-90deg);">
                    <circle cx="50" cy="50" r="45" stroke="rgba(255,255,255,0.1)" stroke-width="8" fill="transparent" />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      stroke="${getScoreColor(scores.overallScore)}"
                      stroke-width="8"
                      fill="transparent"
                      stroke-dasharray="${strokeDasharray}"
                      stroke-dashoffset="${strokeDashoffset}"
                      stroke-linecap="round"
                    />
                  </svg>
                  <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                    <span style="font-size: 24px; font-weight: 800; color: #ffffff;">${scores.overallScore}%</span>
                    <span style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Overall</span>
                  </div>
                </div>

                <div class="gauge-text">
                  <div style="font-size: 16px; font-weight: 700; color: #ffffff;">SEO Health Score</div>
                  <div style="font-size: 11px; color: #94a3b8; margin-bottom: 6px;">Evaluated across ${module.sections.reduce((acc, sec) => acc + sec.steps.length, 0)} technical criteria</div>
                  <span class="badge" style="background: ${getScoreBgColor(scores.overallScore)}; color: ${getScoreColor(scores.overallScore)}; border: 1px solid ${getScoreBorderColor(scores.overallScore)}; align-self: flex-start; font-weight: 700;">
                    ${getScoreLabel(scores.overallScore)}
                  </span>
                </div>
              </div>

              <div class="progress-group">
                <div>
                  <div style="display: flex; justify-content: space-between; font-size: 12px; font-weight: 600; color: #ffffff;">
                    <span>On-Page Technical Score (66% Weight)</span>
                    <span style="color: #34d399;">${scores.onPageScore}%</span>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" style="width: ${scores.onPageScore}%; background: linear-gradient(90deg, #10b981 0%, #34d399 100%);"></div>
                  </div>
                </div>

                <div>
                  <div style="display: flex; justify-content: space-between; font-size: 12px; font-weight: 600; color: #ffffff;">
                    <span>Off-Page Authority Score (34% Weight)</span>
                    <span style="color: #818cf8;">${scores.offPageScore}%</span>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" style="width: ${scores.offPageScore}%; background: linear-gradient(90deg, #6366f1 0%, #818cf8 100%);"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section-graph">
              <div class="section-graph-title">Section Performance Breakdown</div>
              <div class="graph-grid">
                ${module.sections.map(sec => {
                  const secScore = scores.sectionScores[sec.id] ?? 100;
                  const color = getScoreColor(secScore);
                  return `
                    <div class="graph-item">
                      <div style="display: flex; justify-content: space-between; font-size: 11px; font-weight: 600; color: #f8fafc; margin-bottom: 4px;">
                        <span>${sec.title}</span>
                        <span style="color: ${color};">${secScore}%</span>
                      </div>
                      <div class="bar-track" style="height: 4px;">
                        <div class="bar-fill" style="width: ${secScore}%; background: ${color};"></div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          </div>

          <!-- Table of Contents -->
          <div class="toc">
            <div class="toc-title">Table of Contents</div>
            <div class="toc-grid">
              ${module.sections.map((sec, idx) => {
                const secScore = scores.sectionScores[sec.id] ?? 100;
                return `
                  <div class="toc-item">
                    <span>${idx + 1}. ${sec.title}</span>
                    <strong style="color: ${getScoreColor(secScore)};">${secScore}%</strong>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <!-- Executive Summary -->
          <h3 style="font-size: 20px; color: #ffffff; margin-bottom: 12px;">Executive Summary</h3>
          <div class="summary-box">
            ${audit.customExecutiveSummary || `A comprehensive technical SEO audit was conducted for ${audit.websiteUrl} utilizing Zynex Ninja enterprise standards. The overall SEO Health Score was evaluated at ${scores.overallScore}%, highlighting strategic growth opportunities across page titles, meta descriptions, image compression, status codes, and authority links.`}
          </div>

          <!-- Audit Sections -->
          ${module.sections.map((section, secIdx) => {
            const sectionScore = scores.sectionScores[section.id] ?? 100;
            return `
              <div class="sec-header">
                <span>Section ${secIdx + 1}: ${section.title}</span>
                <span class="badge" style="background: ${getScoreBgColor(sectionScore)}; color: ${getScoreColor(sectionScore)}; border: 1px solid ${getScoreBorderColor(sectionScore)}; font-weight: 700;">Score: ${sectionScore}%</span>
              </div>

              ${section.steps.filter(step => step.id !== 'step-meta-keywords').map(step => {
                const answer = audit.answers[step.id];
                if (!answer || !answer.completed) return '';

                const rec = audit.customRecommendations?.[step.id] || step.recommendationTemplate || '';
                const ev = answer.evidence;
                const { standardFields, urlLists } = processStepFields(step, answer.fieldValues, formatFieldValue);

                return `
                  <div class="finding-card">
                    <div class="finding-card-title">
                      <span>${step.stepNumber}. ${step.title}</span>
                      <span class="badge" style="background: rgba(255,255,255,0.1); color: #94a3b8;">${step.tool}</span>
                    </div>

                    ${standardFields.length > 0 ? `
                      <div class="fields-grid">
                        ${standardFields.map(f => `
                          <div class="field-item">
                            <span class="field-label">${f.label}</span>
                            <span class="field-val">${f.formattedValue}</span>
                          </div>
                        `).join('')}
                      </div>
                    ` : ''}

                    ${Object.entries(answer.calculatedValues).map(([, v]) => `
                      <div style="color: #38bdf8; font-size: 13px; font-weight: 600; margin-bottom: 8px;">
                        Calculated Ratio: ${v}
                      </div>
                    `).join('')}

                    ${urlLists.map(urlList => `
                      <div class="url-box">
                        <strong>Affected Page URLs — ${urlList.label} (${urlList.urls.length}):</strong><br/>
                        ${urlList.urls.map(u => `<div>• ${u.trim()}</div>`).join('')}
                      </div>
                    `).join('')}

                    ${(hasRoomForImprovement(step, answer, audit.customRecommendations?.[step.id]) && rec) ? `<div class="rec-box"><strong>Strategic Recommendation:</strong> ${rec}</div>` : ''}

                    ${ev && ev.dataUrl ? `
                      <div style="margin-top: 12px;">
                        <div style="font-size: 11px; color: #94a3b8; font-weight: 600; margin-bottom: 6px;">Verification Evidence (${ev.tool}):</div>
                        <img class="evidence-img" src="${ev.dataUrl}" alt="${ev.caption || step.title}" />
                        ${ev.notes ? `<div style="font-size: 11px; color: #64748b; margin-top: 4px;">Notes: ${ev.notes}</div>` : ''}
                      </div>
                    ` : ''}
                  </div>
                `;
              }).join('')}
            `;
          }).join('')}

          <div class="footer">
            Generated by Zynex Ninja Enterprise SEO Audit Engine
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
}
